import { useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Eraser, PenLine, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "../App";

/**
 * Blocking modal that requires the employee to draw their signature
 * on first login. Cannot be closed until a valid signature is saved.
 */
const SignatureModal = ({ open, onSaved }) => {
  const sigRef = useRef(null);
  const { token, updateUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [accepted, setAccepted] = useState(false);

  if (!open) return null;

  const handleClear = () => {
    sigRef.current?.clear();
    setHasDrawn(false);
  };

  const handleSave = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast.error("Por favor, desenhe sua assinatura antes de salvar.");
      return;
    }
    if (!accepted) {
      toast.error("Você precisa concordar com os termos antes de salvar.");
      return;
    }

    setSaving(true);
    try {
      // Get canvas as base64 PNG (avoid getTrimmedCanvas — broken in alpha)
      const dataUrl = sigRef.current.toDataURL("image/png");

      await axios.post(
        `${API}/employee/signature`,
        { signature_base64: dataUrl },
        { params: { token } }
      );

      updateUser({ has_signature: true });
      toast.success("Assinatura registrada com sucesso!");
      onSaved?.();
    } catch (error) {
      console.error("Error saving signature:", error);
      const detail =
        error?.response?.data?.detail ||
        error?.message ||
        "Erro ao salvar assinatura";
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="signature-modal"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="w-full max-w-xl my-8"
      >
        <Card className="border-0 shadow-2xl">
          <CardContent className="p-5 sm:p-7 space-y-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-5 h-5 text-amber-700" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900 leading-tight">
                  Cadastro obrigatório de Assinatura
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  Para continuar utilizando o sistema, registre sua rúbrica
                  abaixo. Ela será usada como sua assinatura digital nos
                  documentos. Esta etapa é realizada apenas uma vez.
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-2">
              <div className="bg-white rounded-md border border-dashed border-slate-300 relative">
                <SignatureCanvas
                  ref={sigRef}
                  penColor="#0f172a"
                  onEnd={() => setHasDrawn(true)}
                  canvasProps={{
                    className: "w-full h-48 sm:h-56 rounded-md touch-none",
                    "data-testid": "signature-canvas",
                  }}
                />
                {!hasDrawn && (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-slate-300">
                    <div className="flex flex-col items-center gap-1">
                      <PenLine className="w-6 h-6" />
                      <span className="text-xs">Desenhe sua assinatura aqui</span>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between px-1 pt-2">
                <span className="text-xs text-slate-400">
                  Use o dedo (touch) ou o mouse para desenhar.
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClear}
                  disabled={saving}
                  className="text-slate-600 hover:text-slate-900 h-8"
                  data-testid="signature-clear-btn"
                >
                  <Eraser className="w-4 h-4 mr-1" />
                  Limpar
                </Button>
              </div>
            </div>

            <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
                data-testid="signature-accept-checkbox"
              />
              <span>
                Declaro que esta é minha assinatura e autorizo seu uso como
                rúbrica digital nos documentos da AVI Segurança.
              </span>
            </label>

            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <Button
                onClick={handleSave}
                disabled={saving || !hasDrawn || !accepted}
                className="w-full sm:flex-1 bg-slate-900 hover:bg-slate-800"
                data-testid="signature-save-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  "Salvar e continuar"
                )}
              </Button>
            </div>

            <p className="text-xs text-slate-400 text-center">
              Em caso de erro, contate o administrador para liberar um novo
              cadastro.
            </p>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default SignatureModal;
